import '../../sass/global.scss';

import { compose } from 'recompose';
import { connect } from 'react-redux';
import { hot } from 'react-hot-loader';
import cc from 'classcat';

import {
  useComponents,
  ComponentContext,
} from '#src/lib/component-mappings/index.js';
import { staticPageTitleByType } from '#src/lib/routes/pageNames';

import StaticPage from '../StaticPage';
import DynamicPage from '../DynamicPage';
import Notification from '../Notification';
import Toast from '../Toast';
import Metadata from '../Metadata';
import {
  selectCurrentTemplate,
  selectCurrentPage,
} from '../../store/content/selectors';
import { isAuthenticated } from '../../store/session/selectors';
import {
  selectHideFooter,
  selectHideHeader,
  selectLoading,
} from '../../store/page/selectors';
import { selectLanguage } from '../../store/intl/selectors';
import { selectNotification } from '../../store/notifications/selectors';
import { selectToastsByContext } from '../../store/toasts/selectors';
import {
  isDynamicPage,
  selectPageComponent,
  selectPageComponentNamespace,
  selectPageOptions,
  selectPlainFlag,
  selectQuery,
} from '../../store/location/selectors';
import { selectTheme } from '../../store/theme/selectors';
import { selectAssetUrl, selectFields } from '../../lib/contentful/selectors';
import CatchErrorPage from '../CatchErrorPage';
import { ExitIntent, ExitModal } from '..';

import styles from './styles.module.scss';

const App = ({
  template,
  isCorporate,
  productTemplate,
  theme,
  dynamicPage,
  component,
  pageContent,
  allowExitModal,
  contentLoading,
  notification,
  queryMessage,
  toasts,
  usePlainTemplate = false,
  language,
  componentNamespace,
  options,
  staticPageTitle,
  hideFooterOverride,
  hideHeaderOverride,
}) => {
  const {
    pageWrapper: PageWrapper,
    mainHeader: HeaderCmp,
    mainFooter: FooterCmp,
  } = useComponents(pageContent?.componentNamespace || componentNamespace);

  const hideHeader =
    pageContent?.options?.hideHeader ||
    options?.hideHeader ||
    hideHeaderOverride ||
    false;

  const hideFooter =
    pageContent?.options?.hideFooter ||
    options?.hideFooter ||
    hideFooterOverride ||
    false;

  const { pageTitle: dynamicPageTitle, ...contenfulMeta } =
    selectFields(pageContent?.metadata) || {};
  // Dymamic page titles are defined in Contenful
  // Static page titles are defined in lib/statics/index.js
  const pageTitle = dynamicPageTitle || staticPageTitle;

  return (
    <ComponentContext.Provider value={pageContent?.componentNamespace}>
      <PageWrapper className={cc([styles[theme], styles.container])}>
        <Metadata
          favicon={selectAssetUrl(template.favicon)}
          defaultTitle={template.defaultTitle}
          facebookDomainVerification={template.facebookDomainVerification}
          googleSiteVerification={template.googleSiteVerification}
          language={language}
          pageTitle={pageTitle}
          {...contenfulMeta}
        />
        {allowExitModal && (
          <ExitIntent>
            {(intent, onClose) => (
              <ExitModal
                theme={theme}
                visible={intent}
                onClose={onClose}
                content={selectFields(productTemplate.exitIntentModal)}
              />
            )}
          </ExitIntent>
        )}
        {toasts.map((toast) => (
          <Toast key={toast.id} {...toast} />
        ))}
        {queryMessage && <Toast key="queryMessage" message={queryMessage} />}
        {!usePlainTemplate && template && !hideHeader && (
          <HeaderCmp
            corporatePrimaryMenu={template.corporatePrimaryMenu}
            className={styles.header}
            theme={theme}
            logo={
              isCorporate
                ? template.waybetterLogo
                : productTemplate.logo || template.waybetterLogo
            }
            primaryMenu={template.primaryMenu}
            secondaryMenu={template.secondaryMenu}
            dynamicBackground={template.dynamicBackground}
            isCorporate={isCorporate}
          />
        )}
        {notification && <Notification theme={theme} {...notification} />}
        <CatchErrorPage theme={theme}>
          {dynamicPage ? (
            <DynamicPage
              theme={theme}
              content={pageContent}
              loading={contentLoading}
            />
          ) : (
            <StaticPage pageComponent={component} theme={theme} />
          )}
        </CatchErrorPage>
        {!usePlainTemplate && template && !hideFooter && (
          <FooterCmp
            templateStyle={pageContent?.templateStyle}
            theme={theme}
            {...template}
          />
        )}
      </PageWrapper>
    </ComponentContext.Provider>
  );
};

export default compose(
  hot(module),
  connect((state) => {
    const product = selectTheme(state);
    const template = selectCurrentTemplate(state);
    const productTemplate =
      selectFields(
        template.productTemplates?.find(
          (t) => selectFields(t)?.product == product
        )
      ) || {};
    const pageContent = selectCurrentPage(state);
    const allowExitModal =
      !isAuthenticated(state) &&
      !pageContent?.disableExitModal &&
      !!productTemplate.exitIntentModal;

    const isCorporate = pageContent?.templateStyle === 'corporate';

    const staticPageTitle = staticPageTitleByType(state.location.type);

    return {
      component: selectPageComponent(state),
      componentNamespace: selectPageComponentNamespace(state),
      isCorporate,
      template,
      productTemplate,
      theme: product,
      dynamicPage: isDynamicPage(state),
      contentLoading: !pageContent || selectLoading(state),
      pageContent,
      staticPageTitle,
      language: selectLanguage(state),
      notification: selectNotification(state),
      toasts: selectToastsByContext('app')(state),
      queryMessage: selectQuery(state).message,
      usePlainTemplate: selectPlainFlag(state),
      allowExitModal,
      options: selectPageOptions(state),
      hideFooterOverride: selectHideFooter(state),
      hideHeaderOverride: selectHideHeader(state),
    };
  })
)(App);
